/**
 * DOCTOR-WORKSPACE-EXPERIENCE-V2 -- the ONE doctor patient-rows table.
 *
 * A single presentational table shared by the doctor clinic-patients LIST (the
 * Patients page) and the Recent Patients panel (the home) -- never a second
 * table implementation. Doctor-shaped columns ONLY: identity (name / UMR), care
 * demographics (gender / age), and a last-visit / last-seen indicator. NO
 * contact value renders (there is none on the wire); ZERO clinical column
 * (CLINICAL WIRE LAW -- clinical detail is one lens click deeper). Each row
 * opens the EXISTING doctor lens.
 *
 * Pure presentational (no fetch, no state) -- rendered directly in tests.
 */

import { Link } from 'react-router-dom';

import { genderAgeLabel } from '@/dashboard/patients/patient-master-view';
import {
  doctorLensHref,
  doctorLastSeenLabel,
} from '@/dashboard/doctor/doctor-patient-lens-view';

export interface DoctorPatientRow {
  readonly patientId: string;
  readonly umr: string;
  readonly fullName: string | null;
  readonly gender: 'male' | 'female' | 'other' | 'unknown' | null;
  readonly ageYears: number | null;
  readonly ageIsApproximate: boolean;
  /** ISO of the last visit / last activity, or null (honest em-dash). */
  readonly lastAt: string | null;
}

export default function DoctorPatientRows({
  rows,
  lastColumnLabel,
  tableTestId,
  rowTestId,
}: {
  readonly rows: readonly DoctorPatientRow[];
  /** "Last visit" (list) / "Last seen" (recent). */
  readonly lastColumnLabel: string;
  readonly tableTestId: string;
  readonly rowTestId: string;
}): JSX.Element {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table mt-2" data-testid={tableTestId}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Gender / age</th>
            <th>{lastColumnLabel}</th>
            <th>UMR</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.patientId} data-testid={rowTestId}>
              <td className="font-medium text-foreground">
                {r.fullName ?? 'Name pending'}
              </td>
              <td>
                {genderAgeLabel(r.gender ?? 'unknown', r.ageYears, r.ageIsApproximate)}
              </td>
              <td>{doctorLastSeenLabel(r.lastAt)}</td>
              <td className="font-mono text-xs">{r.umr}</td>
              <td>
                <Link
                  to={doctorLensHref(r.patientId, 'search')}
                  className="ac-btn ac-btn--primary ac-btn--sm"
                  data-testid="doctor-patient-open-lens"
                >
                  View details
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
